import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import isStreamVideo from '@anm/helpers/is/isStreamVideo';
import { getLinkTarget, getUpgradeLinkProps } from '@anm/helpers/upgradeLink';
import { FC } from 'react';
import { useSelector } from 'react-redux';
import vtConfig from '../../../config';
import { hostedVideoSelectors } from '../../../store/modules/hostedVideo';
import { projectSelectors } from '../../../store/modules/project';
import { userSelectors } from '../../../store/modules/user';
import { ListElement } from '../../layouts/LeftBarLayout';
import DownloadDropdown from '../DownloadVideo/DownloadDropdown';

import {
  APPEARANCE_TAB,
  BROADCASTS_TAB,
  CustomizationTabIndexes,
  DOMAINS_TAB,
  EMAIL_TAB,
  EMBED_TAB,
  LANDING_TAB,
  PASSWORD_TAB,
  PREVIEW_TAB,
  STATS_TAB
} from '../../../pages/video';
import { Togglers } from './hooks/useTogglers';
import AnalyticsTab from './tabs/AnalyticsTab';
import AppearanceTab from './tabs/AppearanceTab';
import BroadcastTab from './tabs/BroadcastTab';
import EmailTab from './tabs/EmailTab';
import EmbedTab from './tabs/EmbedTab';
import LandingTab from './tabs/LandingTab';
import PermissionsTab from './tabs/PermissionsTab';
import { LayoutProps } from './types';
import { Features } from '@anm/shared/features';
import { selectWaveSubscription } from '../../../store/modules/user/selectors';
import DomainsTab from '../../../components/webinar/DomainsTab';
import { tabMap } from '../../../pages/video';
import { DOWNLOAD_BUTTON_LABEL } from '.';

type GetLayoutNavigationProps = LayoutProps & {
  togglers: Togglers;
  handleSwitchTab: (tab: CustomizationTabIndexes) => void;
  onDownloadStream(streamId: string): void;
};

const DownloadTooltipFreeUser: FC = () => (
  <>
    <span>To download your </span>
    <span>video, please</span>
    <a {...getUpgradeLinkProps()}>upgrade</a>
  </>
);

const DownloadTooltipNotRenderedVideo: FC = () => {
  const projectId = useSelector(projectSelectors.selectCurrentProjectId);
  const editorUrl = `${vtConfig().urls.editor}/#p=${projectId}&target=publish`;

  return (
    <span>
      {isMobileOrIPad() ? (
        'Switch to desktop to publish and download your video.'
      ) : (
        <>
          <a href={editorUrl} target={getLinkTarget()}>
            Publish this video
          </a>
          in the editor to download.
        </>
      )}
    </span>
  );
};

const renderDownloadButton = (onStreamDownload: (streamId: string) => void): ListElement => {
  const canUserDownloadVideo = useSelector(userSelectors.selectCanUserDownloadVideo);
  const videoData = useSelector(hostedVideoSelectors.selectVideoData);

  const isStream = videoData?.sources.some(v => isStreamVideo(v.path));

  return {
    button: {
      title: DOWNLOAD_BUTTON_LABEL,
      icon: 'images/icons/download_icon_v2.svg',
      renderButton: () => <DownloadDropdown {...{ onStreamDownload }} />,
      renderTooltip:
        !isStream && !canUserDownloadVideo
          ? () => <DownloadTooltipFreeUser />
          : !videoData?.sources.length
          ? () => <DownloadTooltipNotRenderedVideo />
          : undefined
    }
  };
};

export const getLayoutNavigation = ({
  sizes,
  hostId,
  projectId,
  videoUrl,
  togglers,
  errorCode,
  videoName,
  currentTime,
  handleSwitchTab,
  onDownloadStream,
  ...changeHandlersProps
}: GetLayoutNavigationProps): ListElement[] => {
  const { embed, landing, password } = togglers;

  const canStreamHosted = useSelector(userSelectors.selectCanStreamHosted);
  const canLiveStream = useSelector(userSelectors.selectCanLiveStream);
  const canRecord = useSelector(userSelectors.selectCanRecord);
  const canUseCustomDomains = Features.canUseCustomDomains(useSelector(selectWaveSubscription));
  const showVideoBroadcastButton = canRecord && canLiveStream && canStreamHosted;

  const broadcastTabTitle = 'Streams & Recordings';

  return [
    {
      button: {
        title: 'Preview',
        icon: 'images/dashboard/home-icon.svg',
        index: tabMap['preview'].index,
        onClick: () => handleSwitchTab(PREVIEW_TAB)
      }
    },
    {
      button: {
        title: 'Landing page',
        icon: 'images/icons/landing_grey_icon.svg',
        index: tabMap['landing'].index,
        switch: {
          ...landing,
          onChange: landing.handler
        },
        onClick: () => handleSwitchTab(LANDING_TAB)
      },
      renderContent: () => <LandingTab {...{ hostId }} />
    },
    {
      button: {
        title: 'Player',
        icon: 'images/dashboard/player-btn-gray-v2.svg',
        index: tabMap['appearance'].index,
        onClick: () => handleSwitchTab(APPEARANCE_TAB)
      },
      renderContent: () => <AppearanceTab {...{ hostId, currentTime }} {...changeHandlersProps} />
    },
    {
      button: {
        title: 'Embed',
        icon: 'images/embed_icon.svg',
        index: tabMap['embed'].index,
        switch: {
          ...embed,
          onChange: embed.handler
        },
        onClick: () => handleSwitchTab(EMBED_TAB)
      },
      renderContent: () => <EmbedTab {...{ hostId, errorCode }} />
    },
    {
      button: {
        title: 'Password',
        icon: 'images/password_icon.svg',
        index: tabMap['permissions'].index,
        switch: {
          ...password,
          onChange: password.handler
        },
        onClick: () => handleSwitchTab(PASSWORD_TAB)
      },
      renderContent: () => <PermissionsTab {...{ hostId }} {...changeHandlersProps} />
    },
    {
      button: {
        title: 'Email campaign',
        icon: 'images/icons/email_icon.svg',
        index: tabMap['email-campaign'].index,
        onClick: () => handleSwitchTab(EMAIL_TAB)
      },
      renderContent: () => <EmailTab {...{ hostId, videoName }} videoSizes={sizes} />
    },
    {
      button: {
        title: 'Analytics',
        icon: 'images/icons/stats_icon.svg',
        index: tabMap['stats'].index,
        onClick: () => handleSwitchTab(STATS_TAB)
      },
      renderContent: () => <AnalyticsTab onSwitchTab={handleSwitchTab} />
    },
    showVideoBroadcastButton && {
      button: {
        title: broadcastTabTitle,
        icon: 'images/icons/broadcast_icon.svg',
        index: tabMap['broadcasts'].index,
        onClick: () => handleSwitchTab(BROADCASTS_TAB)
      },
      renderContent: () => <BroadcastTab title={broadcastTabTitle} />
    },
    canUseCustomDomains && {
      button: {
        title: 'Custom domain',
        icon: 'images/icons/ic_custom_domain.svg',
        index: tabMap['domains'].index,
        onClick: () => handleSwitchTab(DOMAINS_TAB)
      },
      renderContent: () => <DomainsTab {...{ hostId }} />
    },
    renderDownloadButton(onDownloadStream)
  ].filter(Boolean) as ListElement[];
};

export const getLandingTabUrl = (projectId: string) => `${vtConfig().urls.wave}video/${projectId}/landing`;
